import {ErrorHandler, NgModule, inject, provideAppInitializer, APP_ID} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import {LayoutModule} from "./shared/modules/layout/layout.module";
import {AuthGuard} from "./shared/auth/guards/auth.guard";
import {HTTP_INTERCEPTORS, HttpClientModule} from "@angular/common/http";
import {RouterModule} from "@angular/router";
import {JWT_OPTIONS, JwtModule} from "@auth0/angular-jwt";
import {CookieModule, CookieService} from "ngx-cookie";
import {TenantInterceptor} from "./tenant/tenant.interceptor";
import {AppRoutingModule} from "./app-routing.module";
import {TenantConfigService} from "./tenant/services/tenant-config.service";
import {ErrorInterceptorService} from "./shared/interceptors/error-interceptor.service";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {NgSelectModule} from "@ng-select/ng-select";
import {EnvironmentService} from "./shared/services/environment.service";
import {RECAPTCHA_V3_SITE_KEY} from "ng-recaptcha";
import {provideZxvbnServiceForPSM} from "angular-password-strength-meter/zxcvbn";
import {BugsnagErrorHandler} from "@bugsnag/plugin-angular";
import Bugsnag from "@bugsnag/js";
import BugsnagPerformance from "@bugsnag/browser-performance";
import {TenantService} from "./tenant/services/tenant.service";
import {ScriptLoaderModule} from "ngx-script-loader";
import {WINDOW_PROVIDERS} from "./shared/services/window-provider";
import {NotificationBannerComponent} from "./shared/components/notification-banner/notification-banner.component";
import {VersionComponent} from "./shared/modules/version/components/version/version.component";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import { IMAGE_CONFIG, IMAGE_LOADER } from '@angular/common';
import { myCustomLoader } from './shared/modules/image-loader/custom-image-loader';

export function tenantConfigFactory(config: TenantConfigService) {
  return  () => config.load();
}

export function reCaptchaConfigFactory(env: EnvironmentService) {
  return env.tenantConfig?.recaptcha?.sitekey;
}

export function jwtOptionsFactory(cookieService: CookieService) {
  return {
    tokenGetter: () => {
      return cookieService.get("access_token");
    },
    allowedDomains: [
      "api.devel.talentview.org",
      "api.talentview.org",
      "api.devel.destinationnuclearcareersportal.co.uk",
      "api.demo.plus.talentview.org",
      "api.academy.talentview.org",
      "api.devel.thendagroupjobsportal.co.uk",
      "api.thendagroupjobsportal.co.uk",
      "api.staging.v2.talentview.org",
      "localhost:5555",
      "api.staging.jobshop.talentview.org"
    ],
  }
}

// create a factory which will return the Bugsnag error handler
export function errorHandlerFactory(env: EnvironmentService, config: TenantService) {

  // configure Bugsnag ASAP
  Bugsnag.start( {
    appVersion: '1.0.3',
    apiKey: 'ee6f6d7f082043a5a25355263e1d0fc2',
    releaseStage: env.environmentByHost(config.getTenant()),
    enabledReleaseStages: [ 'production', 'develop' ]
  });

  BugsnagPerformance.start({ apiKey: 'ee6f6d7f082043a5a25355263e1d0fc2' })

  return new BugsnagErrorHandler()
}


@NgModule({
  declarations: [
    AppComponent
  ],
    imports: [
        BrowserAnimationsModule,
        CookieModule.withOptions(),
        LayoutModule,
        HttpClientModule,
        RouterModule,
        AppRoutingModule,
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: [CookieService]
            }
        }),
        NgSelectModule,
        ScriptLoaderModule,
        NotificationBannerComponent,
        VersionComponent,
        NgbTooltip
    ],
  providers: [
    TenantConfigService,
    provideAppInitializer(() => {
        const initializerFn = (tenantConfigFactory)(inject(TenantConfigService));
        return initializerFn();
      }),
    WINDOW_PROVIDERS,
    AuthGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptorService,
      multi: true
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      deps: [EnvironmentService],
      useFactory: reCaptchaConfigFactory
    },
    {
      provide: IMAGE_LOADER,
      useValue: myCustomLoader
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        breakpoints: [
          640, 750, 828, 1080, 1200, 1920 
        ]
      }
    },
    provideZxvbnServiceForPSM()
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
